import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/client';
import {
    Button, Card, Field, TextInput, TextArea, Select, Loading, ErrorNote, PageHeader, toast
} from '../components/ui';
import { FileUpload } from '../components/FileUpload';
import { Messaging } from './Messaging';
import { EmailPreview, PreviewToggle } from '../components/TemplatePreview';
import { CANDIDATE_LANGUAGES } from '@aiinterview/shared';

/**
 * The stored category name is an engineering word; this is what the tab says. Only needed where the
 * two differ.
 */
const TAB_LABELS: Record<string, string> = { Messaging: 'SMS & WhatsApp' };

/**
 * Which subject line belongs to which body, so a preview can show the pair a recipient actually
 * gets rather than a body with no heading.
 *
 * Templates absent from here - the signature, the candidate welcome and consent copy - are HTML
 * that is not an email, and get a preview with no subject bar.
 */
const EMAIL_TEMPLATES: Record<string, string> = {
    'Interview.InvitationBodyHtml': 'Interview.InvitationSubject',
    'Interview.ApprovedBodyHtml': 'Interview.ApprovedSubject',
    'Interview.RejectedBodyHtml': 'Interview.RejectedSubject',
    'Interaction.InviteBodyHtml': 'Interaction.InviteSubject'
};

interface SettingRow {
    key: string;
    category: string;
    dataType: string;
    description: string;
    value: string | null;
    /** For a secretref row: the environment variable it names, and whether that is populated. */
    variableName: string | null;
    isConfigured: boolean | null;
}

interface SettingsPayload {
    categories: string[];
    settings: SettingRow[];
}

/** Settings that hold an image path, and so get an upload button and a preview beside the field. */
const IMAGE_SETTINGS = ['Branding.LogoPath', 'Branding.SecondaryLogoPath', 'Branding.FaviconPath'];

/** A setting whose value is a fixed set reads better as a picker than a free-text box. */
/**
 * Settings holding a comma-separated list of a fixed set. Ticked rather than typed: a mistyped code
 * in a free-text box silently disables a language, and nobody finds out until an employee cannot
 * pick it.
 */
const MULTI_CHOICES: Record<string, { value: string; label: string }[]> = {
    'Provider.Speech.EnabledLanguages':
        CANDIDATE_LANGUAGES.map((l) => ({ value: l.code, label: l.label }))
};

const CHOICES: Record<string, string[]> = {
    'Provider.Llm': ['None', 'OpenAI', 'AzureOpenAI', 'OpenRouter'],
    'Provider.Storage': ['LocalDisk', 'AzureBlob'],
    'Provider.Email': ['None', 'Smtp', 'MicrosoftGraph'],
    'Provider.Speech': ['WebSpeech', 'AzureSpeech', 'GoogleSpeech'],
    'Provider.Speech.Language': CANDIDATE_LANGUAGES.map((l) => l.code),
    'Provider.Messaging.SmsMethod': ['POST', 'GET'],
    'Provider.Messaging.WhatsappMethod': ['POST', 'GET']
};

export function Settings() {
    const [category, setCategory] = useState('Branding');
    const [edits, setEdits] = useState<Record<string, string>>({});
    const [error, setError] = useState<ApiError | null>(null);
    const [testAddress, setTestAddress] = useState('');
    const queryClient = useQueryClient();

    const { data, isLoading } = useQuery({
        queryKey: ['settings'],
        queryFn: () => api.get<SettingsPayload>('/api/admin/settings')
    });

    const save = useMutation({
        mutationFn: () => api.post('/api/admin/settings', { values: edits }),
        onSuccess: async () => {
            toast('Settings saved.');
            setEdits({});
            setError(null);
            await queryClient.invalidateQueries({ queryKey: ['settings'] });
            await queryClient.invalidateQueries({ queryKey: ['branding'] });
        },
        onError: (caught: ApiError) => setError(caught)
    });

    /**
     * What the last voice test said, per language. Kept on screen rather than shown as a toast: the
     * useful case is testing several languages and comparing which ones answered.
     */
    const [voiceResults, setVoiceResults] =
        useState<Record<string, { spoke: boolean; voice: string | null; detail: string | null }>>({});
    const [testingVoice, setTestingVoice] = useState('');

    const testVoice = useMutation({
        mutationFn: (language: string) =>
            api.post<{ spoke: boolean; voice: string | null; detail: string | null }>(
                '/api/admin/settings/test-voice', { language }),
        onSuccess: (result, language) =>
            setVoiceResults((prior) => ({ ...prior, [language]: result })),
        onError: (caught: Error, language) => setVoiceResults((prior) => ({
            ...prior, [language]: { spoke: false, voice: null, detail: caught.message }
        }))
    });

    const testEmail = useMutation({
        mutationFn: () => api.post<boolean>('/api/admin/settings/test-email', { to: testAddress }),
        onSuccess: () => toast(`Sent. Check ${testAddress}.`),
        onError: (caught: Error) => toast(caught.message, 'error')
    });

    if (isLoading || !data) return <Loading />;

    const rows = data.settings.filter((s) => s.category === category);

    // Only what this project actually offers. Testing a voice for a language nobody can pick would
    // answer a question no one asked.
    const enabledCodes = (data.settings
        .find((s) => s.key === 'Provider.Speech.EnabledLanguages')?.value ?? '')
        .split(',').map((c) => c.trim()).filter(Boolean);
    const enabledLanguages = CANDIDATE_LANGUAGES.filter((l) => enabledCodes.includes(l.code));
    /**
     * "Branding.CandidateWelcomeHtml" becomes "Candidate Welcome Html". The stored key is an
     * engineering name and recruiters never see one of those; this is the Humanise() the .NET
     * SettingsController used, kept identical so the labels do not drift between the two.
     */
    const label = (key: string) =>
        key.split('.').at(-1)!.replace(/(?<=[a-z0-9])(?=[A-Z])/g, ' ');
    // A secretref row carries its variable name in `variableName`; `value` is always null, because the
    // secret itself never leaves the server. Reading only `value` rendered the box empty, and saving
    // then wrote that empty string over the variable name and silently unconfigured the provider.
    // A `secret` row is write-only: its value is never sent to the browser and the box stays empty
    // on every load, so what is typed is the only thing that can be saved. Blank means "leave the
    // stored one alone", which is what stops an unrelated save on this screen wiping a credential.
    const valueOf = (row: SettingRow) =>
        edits[row.key]
        ?? (row.dataType === 'secret'
            ? ''
            : row.dataType === 'secretref' ? row.variableName : row.value)
        ?? '';
    const set = (key: string, value: string) => setEdits((e) => ({ ...e, [key]: value }));

    /**
     * The current value of any setting by key, whichever category it lives in.
     *
     * A preview needs the subject from this category and the signature from Branding, and both have
     * to reflect unsaved edits - otherwise the preview shows the old wording while the box above it
     * shows the new.
     */
    const valueOfKey = (key: string) =>
        edits[key] ?? data.settings.find((s) => s.key === key)?.value ?? '';

    return (
        <div className="space-y-6">
            {/* The page is "Settings"; the cards below are one category at a time, so the heading
                cannot come from either of them. */}
            <PageHeader
                title="Settings"
                lede="Everything configurable without a redeploy. A change takes effect on the next request."
            />

            <Card>
                <div className="flex flex-wrap gap-2">
                    {data.categories.map((name) => (
                        <button
                            key={name}
                            onClick={() => setCategory(name)}
                            className={`rounded-lg px-3 py-1.5 text-sm transition ${
                                category === name
                                    ? 'bg-[var(--brand-primary)] text-white'
                                    : 'border border-slate-300 text-slate-600 hover:bg-slate-50'
                            }`}
                        >
                            {TAB_LABELS[name] ?? name}
                        </button>
                    ))}
                </div>
            </Card>

            {/* Its own component rather than the generic field list: the gateway presets and the
                test send are what make this screen usable, and neither can be derived from a
                setting's data type. */}
            {category === 'Messaging' ? <Messaging /> : (
            <Card title={category}>
                {error && !error.errors && <ErrorNote message={error.message} />}

                <div className="space-y-5">
                    {rows.map((row) => (
                        <Field
                            key={row.key}
                            label={label(row.key)}
                            hint={row.description}
                            error={error?.fieldError(row.key)}
                        >
                            {/* A secret's value is never sent to the browser. The row shows which
                                environment variable holds it and whether that variable is set. */}
                            {row.dataType === 'secretref' ? (
                                <div className="flex items-center gap-3">
                                    <TextInput
                                        value={valueOf(row)}
                                        onChange={(e) => set(row.key, e.target.value)}
                                        placeholder="Environment variable name"
                                    />
                                    <span
                                        className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                            row.isConfigured
                                                ? 'bg-emerald-100 text-emerald-800'
                                                : 'bg-slate-100 text-slate-600'
                                        }`}
                                    >
                                        {row.isConfigured ? 'Configured' : 'Not configured'}
                                    </span>
                                </div>
                            ) : row.dataType === 'secret' ? (
                                <div className="flex items-center gap-3">
                                    <TextInput
                                        type="password"
                                        autoComplete="new-password"
                                        value={valueOf(row)}
                                        onChange={(e) => set(row.key, e.target.value)}
                                        placeholder={row.isConfigured
                                            ? 'Stored. Type a new one to replace it.'
                                            : 'Not set'}
                                    />
                                    <span
                                        className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                            row.isConfigured
                                                ? 'bg-emerald-100 text-emerald-800'
                                                : 'bg-slate-100 text-slate-600'
                                        }`}
                                    >
                                        {row.isConfigured ? 'Configured' : 'Not configured'}
                                    </span>
                                </div>
                            ) : MULTI_CHOICES[row.key] ? (
                                <div className="flex flex-wrap gap-x-5 gap-y-2">
                                    {MULTI_CHOICES[row.key]!.map((choice) => {
                                        const picked = valueOf(row).split(',')
                                            .map((c) => c.trim()).filter(Boolean);

                                        return (
                                            <label key={choice.value} className="flex items-center gap-2 text-sm">
                                                <input
                                                    type="checkbox"
                                                    checked={picked.includes(choice.value)}
                                                    onChange={(e) => set(row.key, (e.target.checked
                                                        ? [...picked, choice.value]
                                                        // Written back in the order the list
                                                        // declares, not the order they were
                                                        // ticked, so saving twice gives the same
                                                        // string and the diff stays readable.
                                                        : picked.filter((c) => c !== choice.value))
                                                        .sort((a, b) =>
                                                            MULTI_CHOICES[row.key]!.findIndex((m) => m.value === a)
                                                            - MULTI_CHOICES[row.key]!.findIndex((m) => m.value === b))
                                                        .join(','))}
                                                />
                                                {choice.label}
                                            </label>
                                        );
                                    })}
                                </div>
                            ) : CHOICES[row.key] ? (
                                <Select value={valueOf(row)} onChange={(e) => set(row.key, e.target.value)}>
                                    {CHOICES[row.key]!.map((choice) => (
                                        <option key={choice} value={choice}>{choice}</option>
                                    ))}
                                </Select>
                            ) : row.dataType === 'bool' ? (
                                <Select value={valueOf(row)} onChange={(e) => set(row.key, e.target.value)}>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </Select>
                            ) : row.dataType === 'html' ? (
                                <div>
                                    <TextArea
                                        value={valueOf(row)}
                                        onChange={(e) => set(row.key, e.target.value)}
                                        rows={6}
                                    />
                                    {/* Reads from `edits` first, so the preview follows what is on
                                        screen rather than what was last saved. Tuning wording
                                        against a stale preview is the failure this avoids. */}
                                    <PreviewToggle label="Preview">
                                        <EmailPreview
                                            subject={EMAIL_TEMPLATES[row.key]
                                                ? valueOfKey(EMAIL_TEMPLATES[row.key]!)
                                                : undefined}
                                            bodyHtml={valueOf(row)}
                                            signatureHtml={EMAIL_TEMPLATES[row.key]
                                                ? valueOfKey('Branding.EmailSignatureHtml')
                                                : ''}
                                        />
                                    </PreviewToggle>
                                </div>
                            ) : row.dataType === 'color' ? (
                                <div className="flex gap-2">
                                    <input
                                        type="color"
                                        value={valueOf(row) || '#000000'}
                                        onChange={(e) => set(row.key, e.target.value)}
                                        className="h-9 w-14 rounded border border-slate-300"
                                    />
                                    <TextInput
                                        value={valueOf(row)}
                                        onChange={(e) => set(row.key, e.target.value)}
                                        invalid={!!error?.fieldError(row.key)}
                                    />
                                </div>
                            ) : IMAGE_SETTINGS.includes(row.key) ? (
                                // The path stays editable: an installation that already serves its
                                // logo from somewhere else keeps working, and uploading simply fills
                                // this in. Saving the page is still what commits it.
                                <div className="space-y-2">
                                    <TextInput
                                        value={valueOf(row)}
                                        onChange={(e) => set(row.key, e.target.value)}
                                        placeholder="/img/logo.png, or upload one below"
                                        invalid={!!error?.fieldError(row.key)}
                                    />
                                    <FileUpload
                                        purpose="branding"
                                        accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                                        onUploaded={(file) => set(row.key, `/api/assets/${file.path.split('/').pop()}`)}
                                    />
                                    {valueOf(row) && (
                                        <img
                                            src={valueOf(row)}
                                            alt=""
                                            className="max-h-16 rounded border"
                                            style={{ borderColor: 'var(--app-border)' }}
                                        />
                                    )}
                                </div>
                            ) : (
                                <TextInput
                                    type={row.dataType === 'int' || row.dataType === 'decimal' ? 'number' : 'text'}
                                    value={valueOf(row)}
                                    onChange={(e) => set(row.key, e.target.value)}
                                    invalid={!!error?.fieldError(row.key)}
                                />
                            )}
                        </Field>
                    ))}
                </div>

                <div className="mt-6 flex items-center gap-3">
                    <Button
                        onClick={() => save.mutate()}
                        busy={save.isPending}
                        disabled={Object.keys(edits).length === 0}
                    >
                        Save changes
                    </Button>
                    {Object.keys(edits).length > 0 && (
                        <span className="text-xs text-slate-500">
                            {Object.keys(edits).length} unsaved change(s)
                        </span>
                    )}
                </div>
            </Card>
            )}

            {category === 'Provider' && (
                <Card title="Test the question voice">
                    <p className="mb-3 text-sm text-slate-600">
                        Save your changes first — this uses whatever is currently stored. A language
                        that cannot be spoken here falls back to whatever voice the candidate&apos;s
                        browser has, which for most Indian languages is none at all.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {enabledLanguages.map((l) => {
                            const result = voiceResults[l.code];

                            return (
                                <div key={l.code} className="min-w-44">
                                    <Button
                                        variant="secondary"
                                        busy={testVoice.isPending && testingVoice === l.code}
                                        onClick={() => {
                                            setTestingVoice(l.code);
                                            testVoice.mutate(l.code);
                                        }}
                                    >
                                        {l.label}
                                    </Button>
                                    {result && (
                                        <p
                                            className="mt-1 text-xs"
                                            style={{
                                                color: result.spoke
                                                    ? 'var(--text-muted-safe)'
                                                    : 'var(--danger, #b91c1c)'
                                            }}
                                        >
                                            {result.spoke
                                                ? result.voice ?? 'Spoke.'
                                                : result.detail ?? 'Nothing came back.'}
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </Card>
            )}

            {category === 'Provider' && (
                <Card title="Send a test email">
                    <p className="mb-3 text-sm text-slate-600">
                        Save your changes first — this uses whatever is currently stored.
                    </p>
                    <div className="flex max-w-md gap-2">
                        <TextInput
                            value={testAddress}
                            onChange={(e) => setTestAddress(e.target.value)}
                            placeholder="you@example.com"
                        />
                        <Button
                            variant="secondary"
                            busy={testEmail.isPending}
                            disabled={!testAddress}
                            onClick={() => testEmail.mutate()}
                        >
                            Send
                        </Button>
                    </div>
                </Card>
            )}
        </div>
    );
}
